import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Play, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { mh } from "@/lib/mh";
import { useAuth } from "@/context/AuthContext";
import { linkIndex } from "@/lib/sky";
import { SavingsCard } from "@/components/mh/SavingsCard";

const Links = ({ label, slugs, agents, color, onSwitch, k }) => {
  const items = slugs.map((s) => agents.find((a) => a.slug === s)).filter(Boolean);
  if (!items.length) return null;
  return (
    <div data-testid={`role-panel-${k}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color }}>{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((a) => <button key={a.slug} onClick={() => onSwitch(a.slug)} data-testid={`role-panel-link-${a.slug}`} className="h-7 px-3 rounded-full border border-white/10 text-[11px] hover:border-white/30 transition-colors">{a.name}</button>)}
      </div>
    </div>
  );
};

const Tailoring = ({ t }) => (
  <section className="rounded-xl border border-white/[0.07] p-4 text-xs space-y-1.5" data-testid="role-panel-tailoring">
    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#00E5FF]">Tailored for you</div>
    <div className="text-muted-foreground">Fit: <span className="text-foreground">{t.fit}</span>{t.fit_reason ? ` · ${t.fit_reason}` : ""}</div>
    <div className="text-muted-foreground">Status: <span className="text-foreground">{t.enabled ? "On" : "Off"}</span>{t.wave ? ` · wave ${t.wave}` : ""}</div>
    <div className="text-muted-foreground">Owner: <span className="text-foreground">{t.owner_email || "—"}</span></div>
  </section>
);

export const RolePanel = ({ slug, agents, onClose, onSwitch }) => {
  const [a, setA] = useState(null);
  const { canManage } = useAuth();
  const idx = useMemo(() => linkIndex(agents), [agents]);
  useEffect(() => { setA(null); mh.agent(slug).then((r) => setA(r.data)).catch(() => toast.error("Couldn't load this role")); }, [slug]);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const run = () => mh.runAgent(slug).then(() => toast.success("Run dispatched")).catch((e) => toast.error(e.response?.data?.detail || "Run failed"));
  const hex = a?.department_info?.hex || "#E8E8EE";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="role-panel-overlay">
      <aside key={slug} className="mh-slide-in w-full sm:w-[520px] h-full overflow-y-auto bg-[#0A0A0F] border-r border-white/10 p-6 sm:p-8 space-y-6" data-testid="role-panel">
        <div className="flex justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: hex }} data-testid="role-panel-autonomy">{a?.autonomy || "…"}</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-2" data-testid="role-panel-name">{a?.name || "Loading…"}</h2>
            {a && <div className="text-sm text-muted-foreground mt-1">{a.department_info?.name} · {a.stage}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="role-panel-close" className="w-10 h-10 shrink-0 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {a && <>
          <p className="text-base">{a.one_liner}</p>
          <SavingsCard key={a.slug} a={a} />
          {a.tailored && <Tailoring t={a.tailored} />}
          <div className="space-y-4">
            <Links k="requires" label="Requires" color="#00E5FF" slugs={idx.req(slug)} agents={agents} onSwitch={onSwitch} />
            <Links k="enables" label="Enables" color="#D4FF3A" slugs={idx.en(slug)} agents={agents} onSwitch={onSwitch} />
            <Links k="related" label="Related" color="#9A9AAE" slugs={idx.rel(slug)} agents={agents} onSwitch={onSwitch} />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Link to={`/atlas/${slug}`} data-testid="role-panel-full" className="h-10 px-4 rounded-full bg-white text-black text-sm flex items-center gap-2 hover:bg-white/90 transition-colors"><ExternalLink className="w-4 h-4" />Open full page</Link>
            {canManage && <button onClick={run} data-testid="role-panel-run" className="h-10 px-4 rounded-full border border-white/15 text-sm flex items-center gap-2 hover:bg-white/5 transition-colors"><Play className="w-4 h-4" />Run now</button>}
          </div>
        </>}
      </aside>
      <button className="flex-1 bg-black/50 backdrop-blur-sm hidden sm:block" onClick={onClose} aria-label="Close panel" data-testid="role-panel-backdrop" />
    </div>
  );
};
